import React from "react";
import {
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  IconButton
} from "@mui/material";
import { Add, Delete } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { sharedStyles } from '../styles/sharedStyles';

const defaultCondition = () => ({
  fullname: '',
  illness: '',
  treated: '',
  gpfullname: '',
  firsttreatmentdate: '',
  lasttreatmentdate: '',
  medication: ''
});

const Step7MedicalHistory = ({ formData, setFormData, errors = {}, setErrors, handleNext, handleBack }) => {
  const hasMedicalConditions = formData.hasMedicalConditions || 'No';
  const q1 = formData.q1 || 'No';
  const q2 = formData.q2 || 'No';
  const q3 = formData.q3 || 'No';
  const q4 = formData.q4 || 'No';
  const conditions = formData.medical_history || [];
  const addForQuestion = formData.addForQuestion || null;

  const handleRadio = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  // Map UI field names to API keys for medical history
  const condMapping = {
    memberName: 'fullname',
    illness: 'illness',
    beingTreated: 'treated',
    treatingDoctor: 'gpfullname',
    dateFirstTreatment: 'firsttreatmentdate',
    dateLastTreatment: 'lasttreatmentdate',
    medication: 'medication',
  };
  const handleConditionChange = (idx, field, value) => {
    const apiKey = condMapping[field] || field;
    const updated = conditions.map((c, i) => i === idx ? { ...c, [apiKey]: value } : c);
    setFormData({ ...formData, medical_history: updated });
    setErrors({ ...errors, [`condition${idx+1}_${field}`]: undefined });
  };
  const handleConditionDateChange = (idx, field, date) => {
    const apiKey = condMapping[field] || field;
    let formatted = '';
    if (date instanceof Date && !isNaN(date)) {
      formatted = date.toISOString().split('T')[0];
    }
    const updated = conditions.map((c, i) => i === idx ? { ...c, [apiKey]: formatted } : c);
    setFormData({ ...formData, medical_history: updated });
    setErrors({ ...errors, [`condition${idx+1}_${field}`]: undefined });
  };

  const addConditionForQuestion = (questionNum) => {
    const newCondition = { ...defaultCondition(), question: questionNum };
    setFormData({ ...formData, medical_history: [...conditions, newCondition], addForQuestion: null });
  };

  const removeCondition = (idx) => setFormData({ ...formData, medical_history: conditions.filter((_, i) => i !== idx) });

  const onNext = (e) => {
    e.preventDefault();
    handleNext();
  };

  return (
    <Box component="form" onSubmit={onNext} autoComplete="off" sx={sharedStyles.formContainer}>
      <Typography variant="h5" sx={sharedStyles.stepTitle}>
        MEDICAL HISTORY DETAILS
      </Typography>
      <Paper sx={sharedStyles.card}>
        <Typography sx={{ mb: 2, fontWeight: 500 }}>
          Do you have any medical conditions to declare?
        </Typography>
        <RadioGroup row value={hasMedicalConditions} onChange={handleRadio('hasMedicalConditions')}>
          <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
          <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
        </RadioGroup>
        <Typography sx={{ mt: 2, mb: 2, fontStyle: 'italic' }}>
          All medical questions must be answered with a "Yes" or "No". If "Yes", please provide full details of all pre-existing conditions. It is compulsory to answer these questions as it will impact underwriting. You need to answer these questions accurately and completely. Failure to make full disclosure could result in one or more of the following consequences, which may be adverse to you and your dependants: the Scheme may fail to process your application, or it may cancel your membership retrospectively from the date of inception or potentially reverse claims already paid. We therefore caution you to take particular care in making full disclosure of any medical concerns.
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Q1 */}
            <Grid item xs={12} md={10}>
              <Typography>1. Have you or any of your dependants sought advice or has been diagnosed or treated for any medical or surgical conditions in the past 12 months? (Example: back injury, kidney dialysis, pneumonia)</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <RadioGroup row value={q1} onChange={handleRadio('q1')}>
                <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
              </RadioGroup>
            </Grid>
            {q1 === 'Yes' && (
              <>
                <Grid item xs={12}>
                  <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => addConditionForQuestion('q1')} sx={{ minWidth: 120, mt: 1 }}>
                    Add
                  </Button>
                </Grid>
                {/* Render forms for q1 */}
                {conditions.filter(c => c.question === 'q1').map((cond, idx) => (
                  <Grid item xs={12} key={`q1-form-${idx}`}>
                    <Paper sx={{ p: 2, mb: 3, position: 'relative', background: '#fafbfc' }}>
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton onClick={() => removeCondition(conditions.findIndex(c => c === cond))} color="error" size="small"><Delete /></IconButton>
                      </Box>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Member {idx + 1}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Member Name" name="fullname" value={cond.fullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'fullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_fullname`]} helperText={errors[`condition${idx+1}_fullname`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Illness" name="illness" value={cond.illness || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'illness', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_illness`]} helperText={errors[`condition${idx+1}_illness`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ mb: 1, fontWeight: 500 }}>Are You Being Treated</Typography>
                          <RadioGroup row name="treated" value={cond.treated || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'treated', e.target.value)}>
                            <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                            <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Date of First Treatment"
                              value={cond.firsttreatmentdate ? new Date(cond.firsttreatmentdate) : null}
                              onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'firsttreatmentdate', date)}
                              renderInput={(params) => (
                                <TextField {...params} name="firsttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_firsttreatmentdate`]} helperText={errors[`condition${idx+1}_firsttreatmentdate`]} />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                        {cond.treated === 'Yes' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label="Date of Last Treatment"
                                  value={cond.lasttreatmentdate ? new Date(cond.lasttreatmentdate) : null}
                                  onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'lasttreatmentdate', date)}
                                  renderInput={(params) => (
                                    <TextField {...params} name="lasttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_lasttreatmentdate`]} helperText={errors[`condition${idx+1}_lasttreatmentdate`]} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Treating Doctor" name="gpfullname" value={cond.gpfullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'gpfullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_gpfullname`]} helperText={errors[`condition${idx+1}_gpfullname`]} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Medication" name="medication" value={cond.medication || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'medication', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_medication`]} helperText={errors[`condition${idx+1}_medication`]} />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </>
            )}
            {/* Q2 */}
            <Grid item xs={12} md={10}>
              <Typography>2. Do you, or any of your dependants take any chronic medication at this stage or are expecting to take medicine on an ongoing basis in the near future? (Example: chronic medicine for diabetes, hypertension, asthma)</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <RadioGroup row value={q2} onChange={handleRadio('q2')}>
                <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
              </RadioGroup>
            </Grid>
            {q2 === 'Yes' && (
              <>
                <Grid item xs={12}>
                  <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => addConditionForQuestion('q2')} sx={{ minWidth: 120, mt: 1 }}>
                    Add
                  </Button>
                </Grid>
                {/* Render forms for q2 */}
                {conditions.filter(c => c.question === 'q2').map((cond, idx) => (
                  <Grid item xs={12} key={`q2-form-${idx}`}>
                    <Paper sx={{ p: 2, mb: 3, position: 'relative', background: '#fafbfc' }}>
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton onClick={() => removeCondition(conditions.findIndex(c => c === cond))} color="error" size="small"><Delete /></IconButton>
                      </Box>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Member {idx + 1}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Member Name" name="fullname" value={cond.fullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'fullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_fullname`]} helperText={errors[`condition${idx+1}_fullname`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Illness" name="illness" value={cond.illness || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'illness', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_illness`]} helperText={errors[`condition${idx+1}_illness`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ mb: 1, fontWeight: 500 }}>Are You Being Treated</Typography>
                          <RadioGroup row name="treated" value={cond.treated || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'treated', e.target.value)}>
                            <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                            <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Date of First Treatment"
                              value={cond.firsttreatmentdate ? new Date(cond.firsttreatmentdate) : null}
                              onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'firsttreatmentdate', date)}
                              renderInput={(params) => (
                                <TextField {...params} name="firsttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_firsttreatmentdate`]} helperText={errors[`condition${idx+1}_firsttreatmentdate`]} />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                        {cond.treated === 'Yes' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label="Date of Last Treatment"
                                  value={cond.lasttreatmentdate ? new Date(cond.lasttreatmentdate) : null}
                                  onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'lasttreatmentdate', date)}
                                  renderInput={(params) => (
                                    <TextField {...params} name="lasttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_lasttreatmentdate`]} helperText={errors[`condition${idx+1}_lasttreatmentdate`]} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Treating Doctor" name="gpfullname" value={cond.gpfullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'gpfullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_gpfullname`]} helperText={errors[`condition${idx+1}_gpfullname`]} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Medication" name="medication" value={cond.medication || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'medication', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_medication`]} helperText={errors[`condition${idx+1}_medication`]} />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </>
            )}
            {/* Q3 */}
            <Grid item xs={12} md={10}>
              <Typography>3. Are you or any of your dependents planning or reasonably expecting to be hospitalised or to have a procedure or treatment in the next 12 months? (Example: pregnancy, gastroscopy, MRI scans, chemotherapy)</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <RadioGroup row value={q3} onChange={handleRadio('q3')}>
                <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
              </RadioGroup>
            </Grid>
            {q3 === 'Yes' && (
              <>
                <Grid item xs={12}>
                  <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => addConditionForQuestion('q3')} sx={{ minWidth: 120, mt: 1 }}>
                    Add
                  </Button>
                </Grid>
                {/* Render forms for q3 */}
                {conditions.filter(c => c.question === 'q3').map((cond, idx) => (
                  <Grid item xs={12} key={`q3-form-${idx}`}>
                    <Paper sx={{ p: 2, mb: 3, position: 'relative', background: '#fafbfc' }}>
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton onClick={() => removeCondition(conditions.findIndex(c => c === cond))} color="error" size="small"><Delete /></IconButton>
                      </Box>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Member {idx + 1}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Member Name" name="fullname" value={cond.fullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'fullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_fullname`]} helperText={errors[`condition${idx+1}_fullname`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Illness" name="illness" value={cond.illness || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'illness', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_illness`]} helperText={errors[`condition${idx+1}_illness`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ mb: 1, fontWeight: 500 }}>Are You Being Treated</Typography>
                          <RadioGroup row name="treated" value={cond.treated || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'treated', e.target.value)}>
                            <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                            <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Date of First Treatment"
                              value={cond.firsttreatmentdate ? new Date(cond.firsttreatmentdate) : null}
                              onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'firsttreatmentdate', date)}
                              renderInput={(params) => (
                                <TextField {...params} name="firsttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_firsttreatmentdate`]} helperText={errors[`condition${idx+1}_firsttreatmentdate`]} />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                        {cond.treated === 'Yes' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label="Date of Last Treatment"
                                  value={cond.lasttreatmentdate ? new Date(cond.lasttreatmentdate) : null}
                                  onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'lasttreatmentdate', date)}
                                  renderInput={(params) => (
                                    <TextField {...params} name="lasttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_lasttreatmentdate`]} helperText={errors[`condition${idx+1}_lasttreatmentdate`]} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Treating Doctor" name="gpfullname" value={cond.gpfullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'gpfullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_gpfullname`]} helperText={errors[`condition${idx+1}_gpfullname`]} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Medication" name="medication" value={cond.medication || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'medication', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_medication`]} helperText={errors[`condition${idx+1}_medication`]} />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </>
            )}
            {/* Q4 */}
            <Grid item xs={12} md={10}>
              <Typography>4. Are there any other conditions or symptoms not mentioned above for which medical advice, diagnosis, care or treatment has been recommended or received, or could potentially result in a medical claim in the next 12 months that you would like to disclose?</Typography>
            </Grid>
            <Grid item xs={12} md={2}>
              <RadioGroup row value={q4} onChange={handleRadio('q4')}>
                <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
              </RadioGroup>
            </Grid>
            {q4 === 'Yes' && (
              <>
                <Grid item xs={12}>
                  <Button variant="contained" color="primary" startIcon={<Add />} onClick={() => addConditionForQuestion('q4')} sx={{ minWidth: 120, mt: 1 }}>
                    Add
                  </Button>
                </Grid>
                {/* Render forms for q4 */}
                {conditions.filter(c => c.question === 'q4').map((cond, idx) => (
                  <Grid item xs={12} key={`q4-form-${idx}`}>
                    <Paper sx={{ p: 2, mb: 3, position: 'relative', background: '#fafbfc' }}>
                      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                        <IconButton onClick={() => removeCondition(conditions.findIndex(c => c === cond))} color="error" size="small"><Delete /></IconButton>
                      </Box>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Member {idx + 1}</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Member Name" name="fullname" value={cond.fullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'fullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_fullname`]} helperText={errors[`condition${idx+1}_fullname`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField label="Illness" name="illness" value={cond.illness || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'illness', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_illness`]} helperText={errors[`condition${idx+1}_illness`]} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Typography sx={{ mb: 1, fontWeight: 500 }}>Are You Being Treated</Typography>
                          <RadioGroup row name="treated" value={cond.treated || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'treated', e.target.value)}>
                            <FormControlLabel value="Yes" control={<Radio color="primary" />} label="YES" />
                            <FormControlLabel value="No" control={<Radio color="primary" />} label="NO" />
                          </RadioGroup>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="Date of First Treatment"
                              value={cond.firsttreatmentdate ? new Date(cond.firsttreatmentdate) : null}
                              onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'firsttreatmentdate', date)}
                              renderInput={(params) => (
                                <TextField {...params} name="firsttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_firsttreatmentdate`]} helperText={errors[`condition${idx+1}_firsttreatmentdate`]} />
                              )}
                            />
                          </LocalizationProvider>
                        </Grid>
                        {cond.treated === 'Yes' && (
                          <>
                            <Grid item xs={12} sm={6}>
                              <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DatePicker
                                  label="Date of Last Treatment"
                                  value={cond.lasttreatmentdate ? new Date(cond.lasttreatmentdate) : null}
                                  onChange={date => handleConditionDateChange(conditions.findIndex(c => c === cond), 'lasttreatmentdate', date)}
                                  renderInput={(params) => (
                                    <TextField {...params} name="lasttreatmentdate" fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_lasttreatmentdate`]} helperText={errors[`condition${idx+1}_lasttreatmentdate`]} />
                                  )}
                                />
                              </LocalizationProvider>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Treating Doctor" name="gpfullname" value={cond.gpfullname || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'gpfullname', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_gpfullname`]} helperText={errors[`condition${idx+1}_gpfullname`]} />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <TextField label="Medication" name="medication" value={cond.medication || ''} onChange={e => handleConditionChange(conditions.findIndex(c => c === cond), 'medication', e.target.value)} fullWidth {...sharedStyles.inputProps} error={!!errors[`condition${idx+1}_medication`]} helperText={errors[`condition${idx+1}_medication`]} />
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </>
            )}
          </Grid>
        </Box>
      </Paper>
    </Box>
  );
};

export default Step7MedicalHistory; 